import React, { useEffect, useState } from 'react';
import UnifiedNavigation, { NavigationVariant } from './UnifiedNavigation';
import { useScrollManager } from '../../hooks/useScrollManager';
import { useOverlayManager } from '../../contexts/OverlayContext';

interface SmartNavigationProps {
  /** Default variant when not scrolled */
  defaultVariant?: NavigationVariant;
  /** Variant to switch to when scrolled */
  scrolledVariant?: NavigationVariant;
  /** Whether to automatically switch variants on scroll */
  autoSwitchOnScroll?: boolean;
  /** Force a specific variant (disables auto-switching) */
  forceVariant?: NavigationVariant;
  /** Additional CSS classes */
  className?: string;
  /** Force white assets (for homepage) */
  forceWhiteAssets?: boolean;
}

/**
 * SmartNavigation automatically switches between navigation variants
 * based on scroll state and page context
 * 
 * Global Preferences:
 * - Language dropdown must remain fully opaque on hover (no opacity changes)
 * - Navigation ignores scroll state when overlays are open
 */
const SmartNavigation: React.FC<SmartNavigationProps> = ({
  defaultVariant = 'transparent',
  scrolledVariant = 'light',
  autoSwitchOnScroll = true,
  forceVariant,
  className = '',
  forceWhiteAssets = false
}) => {
  const { scrollState } = useScrollManager();
  const { overlayState } = useOverlayManager();
  const [currentVariant, setCurrentVariant] = useState<NavigationVariant>(
    forceVariant || defaultVariant
  );

  useEffect(() => {
    // If variant is forced, don't auto-switch
    if (forceVariant) {
      setCurrentVariant(forceVariant);
      return;
    }

    // If menu or resort navigation is open, don't change variant based on scroll
    if (overlayState.isMenuOpen || overlayState.isResortNavOpen) {
      return;
    }

    // Auto-switch based on scroll state
    if (autoSwitchOnScroll) {
      setCurrentVariant(scrollState.isScrolled ? scrolledVariant : defaultVariant);
    }
  }, [
    scrollState.isScrolled, 
    autoSwitchOnScroll, 
    defaultVariant, 
    scrolledVariant, 
    forceVariant,
    overlayState.isMenuOpen,
    overlayState.isResortNavOpen
  ]);

  // Don't apply scroll classes when overlays are open
  const isOverlayOpen = overlayState.isMenuOpen || overlayState.isResortNavOpen;
  const scrollClasses = isOverlayOpen 
    ? 'nav-scroll-transition' 
    : `${scrollState.isScrolled ? 'scrolled nav-scroll-transition' : 'nav-scroll-transition'}`;

  // Hide the page navigation when menu overlay or resort navigation is open
  if (isOverlayOpen) {
    return null;
  }

  return (
    <UnifiedNavigation 
      variant={currentVariant}
      className={`${className} ${scrollClasses}`}
      forceWhiteAssets={forceWhiteAssets && !scrollState.isScrolled && !isOverlayOpen}
    />
  );
};

export default SmartNavigation;